import React, { FC } from 'react'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import GenericText from 'components/common/GenericText'
import GenericHeading from 'components/common/GenericHeading'
import { useTheme, SxProps } from '@mui/material/styles'
import { NOT_AVAILABLE } from 'utils/constants'
import Income from 'components/Income/Income'
import { useCurrencyConversion } from 'hooks/useCurrencyConversion'

interface FinancialRowProps {
  heading: string
  revenues: (number | string | null)[]
  valueSx?: SxProps
}

const FinancialRow: FC<FinancialRowProps> = ({ heading, revenues, valueSx = {} }) => {
  const theme = useTheme()
  const { currentCurrency } = useCurrencyConversion()

  const getValue = (value: number | string | null, index: number) => {
    if (value === null) return NOT_AVAILABLE

    if (heading === '') {
      return value.toString()
    }

    if (typeof value === 'string') {
      return value
    }

    if (isNaN(value)) {
      return NOT_AVAILABLE
    }

    return <Income value={value} useCurrencyConversion={false} isFormatted={true} currency={currentCurrency} />
  }

  return (
    <Stack direction='row' justifyContent='space-between' spacing={2}>
      <Box>
        <GenericHeading
          sx={{
            color: '#475467',
            fontWeight: 600,
            fontFamily: 'Source Sans Pro',
            fontSize: { lg: 11, xl: 13, xlplus: 20, xxxl: 32 },
            width: { lg: 74, xl: 92, xlplus: 130, xxxl: 200 },
            [theme.breakpoints.down('xl')]: {
              width: '74px',
              fontSize: '12px',
            },
          }}
        >
          {heading}
        </GenericHeading>
      </Box>
      <Stack component='ul' direction='row' sx={{ flexBasis: '80%', justifyContent: 'space-evenly' }}>
        {revenues.map((revenue, index) => (
          <Stack
            component='li'
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            key={`revenue-${index}`}
          >
            <GenericText
              sx={{
                fontFamily: 'Dosis',
                fontSize: 16,
                width: 80,
                textAlign: 'center',
                [theme.breakpoints.down('xl')]: {
                  fontSize: 12,
                  width: 60,
                },
                [theme.breakpoints.up('xlplus')]: {
                  fontSize: 20,
                },
                [theme.breakpoints.up('xxxl')]: {
                  fontSize: 30,
                  width: 120,
                },
                ...valueSx,
              }}
            >
              {getValue(revenue, index)}
            </GenericText>
          </Stack>
        ))}
      </Stack>
    </Stack>
  )
}

export default FinancialRow
